/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { Validators } from '@angular/forms';
import { type Command, type JobResult, type Rest, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, Status } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type WindowsLocalGroup } from 'app/common/types/mo';
import { SWITCH } from 'app/config';

import { type Privilege } from '../permit-table/permit-table';
import { WindowsLocalAccessGroupService } from '../windows-local-access-group.service';
import { nameFormatValidator, ridRange, WindowsAccessUserGroupStrService } from '../windows-local-access-user-group-str.service';

@Component({
    selector: 'app-create-windows-local-access-group',
    templateUrl: './create-windows-local-access-group.html',
    styleUrls: ['../style.scss'],
})
export class CreateWindowsLocalAccessGroupComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() vstore: SelectOption;
    i18n = this.str.i18n;

    windowVisible = true;

    vm: FormGroup;
    errorMessage: any;

    vstoreRest: Rest;
    ridRange = ridRange;
    SWITCH = SWITCH;

    permitList: Privilege[] = [];
    allPermit: Privilege[] = this.str.allPermit;

    get vmValue(): WindowsLocalGroup {
        return this.vm ? this.vm.value : null;
    }

    constructor(
        private localGroupService: WindowsLocalAccessGroupService,
        public str: WindowsAccessUserGroupStrService,
        injector: Injector,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.vstoreRest = this.localGroupService.getVstoreRest();
    }

    initVm(): void {
        this.vm = new FormGroup({
            vstoreId: new FormControl(this.vstore && this.vstore.value ? this.vstore.value : null, {
                validators: [
                    Validators.required,
                ],
            }),
            NAME: new FormControl('', [
                Validators.required,
                DMValidators.rangeByteLength(1, 256),
                nameFormatValidator,
            ]),
            DESCRIPTION: new FormControl('', [
                DMValidators.rangeByteLength(0, 256),
            ]),
        }, {
            updateOn: 'blur',
        });

        this.errorMessage = {
            vstoreId: {
                required: this.toolService.requiredErr(this.i18n.vStoreLab),
            },
            NAME: {
                required: this.toolService.requiredErr(this.i18n.name),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 256),
                nameFormat: this.i18n.nameFormatErr,
            },
            DESCRIPTION: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 256),
            },
        };
    }

    onDataChange = (data: Privilege[]): void => {
        this.permitList = data;
    };

    buildCmds(): Command[] {
        const cmds: Command[] = [this.buildCreatelGroupCmd()];
        return cmds;
    }

    buildCreatelGroupCmd(): Command {
        const vmValue = this.vmValue;
        // 特权初始值，0代表无特权
        let privileges = '0';
        const params: WindowsLocalGroup = {
            NAME: vmValue.NAME,
            DESCRIPTION: vmValue.DESCRIPTION,
        };

        params.vstoreId = vmValue.vstoreId || this.vstore.value;
        if (!params.vstoreId) {
            delete params.vstoreId;
        }

        if (this.permitList && this.permitList.length > 0) {
            privileges = this.permitList.map((item: Privilege) => item.id).join(',');
        }

        params.PRIVILEGES = privileges;

        return {
            name: this.i18n.createGroupResult(params.NAME),
            action: this.localGroupService.create(params),
        };
    }

    onComplete = (results: JobResult[]): void => {
        const userData: WindowsLocalGroup[] = [];
        results.forEach((item) => {
            if (item.status === Status.success && item.response.data && item.response.data.NAME) {
                userData.push(item.response.data);
            }
        });
        this.closeArgs.data = userData;
        this.closeArgs.refresh = results.some((a) => a.status === Status.success);
    };
}
